import { useState, type SyntheticEvent } from 'react';
import { appTitle } from '../lib/brand.ts';
import type { Language, MessageKey } from '../lib/i18n.ts';
import { supabase } from '../supabase.ts';

interface Props {
  readonly language: Language;
  readonly t: (key: MessageKey) => string;
  readonly onToggleLanguage: () => void;
}

export function Login({ language, t, onToggleLanguage }: Props) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setState('sending');
    // Invite-only: shouldCreateUser=false means unknown addresses get no account.
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
    });
    setState(error ? 'failed' : 'sent');
  };

  return (
    <main className="login">
      <div className="login-panel">
        <span className="brand">
          <img className="brand-logo" src="/icons/icon-192.png" alt="" width={28} height={28} />
          {appTitle(language)}
        </span>
        <h1>{t('loginTitle')}</h1>
        <p className="muted">{t('loginHint')}</p>
        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span>{t('email')}</span>
            <input
              type="email"
              required
              autoComplete="email"
              dir="ltr"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </label>
          <button type="submit" className="button primary" disabled={state === 'sending'}>
            {t('sendLink')}
          </button>
        </form>
        {state === 'sent' && <p role="status">{t('linkSent')}</p>}
        {state === 'failed' && (
          <p role="alert" className="error-text">
            {t('loginFailed')}
          </p>
        )}
        <button type="button" className="link-button" onClick={onToggleLanguage}>
          {t('switchLanguage')}
        </button>
      </div>
    </main>
  );
}
